// src/config/seo.ts
//
// Per-route document metadata. The site is a single-page app served from
// static storage, so without this every URL carried the same title and
// description — nothing for search engines or link previews to show.
// Applied at runtime by <SeoWrapper> (src/components/common/SeoWrapper.tsx);
// the same route list feeds the build-time sitemap (vite.config.ts).

import { brand } from './brand';

export interface RouteMeta {
  title: string;
  description: string;
  /** Exclude from the sitemap / ask crawlers not to index (auth-only pages). */
  noindex?: boolean;
}

const product = brand.productName;

export const ROUTE_META: Record<string, RouteMeta> = {
  '/': {
    title: `${product} — Be the CEO of your own AI team`,
    description: `Download ${product} and deploy AI agents that handle your workload: coding, research, operations. Free to start, every AI model included, runs on Windows, macOS and Linux.`,
  },
  '/pricing': {
    title: `Pricing — ${product}`,
    description: `Start free with 100,000 credit points and no card. ${product} plans from $19.99/month, every AI model included, pay-as-you-go top-ups any time.`,
  },
  '/gallery': {
    title: `Community gallery — apps built with ${product}`,
    description: `Browse applications the community built with ${product} AI agents, vote for favourites and submit your own.`,
  },
  '/docs': {
    title: `Documentation — ${product}`,
    description: `Install ${product}, sign in, create your first agent and connect your tools. Guides and reference for the desktop app and the command line.`,
  },
  '/training': {
    title: `Video tutorials — ${product}`,
    description: `Short video walkthroughs: installing ${product}, briefing your first agent, building a squadron and delegating real work.`,
  },
  '/about': {
    title: `About — ${product}`,
    description: `Who builds ${product} and why: Loxia Labs on autonomous AI teams for people who run things.`,
  },
  '/contact': {
    title: `Contact — ${product}`,
    description: `Questions, partnerships or support for ${product}. Reach the Loxia Labs team.`,
  },
  '/register': {
    title: `Create your free account — ${product}`,
    description: `Sign up for ${product} in under a minute. 100,000 credit points included, no card needed.`,
  },
  '/login': {
    title: `Sign in — ${product}`,
    description: `Sign in to your ${product} account.`,
  },
  '/terms': { title: `Terms of use — ${product}`, description: `Terms of use for ${product} by Loxia Labs.` },
  '/privacy': { title: `Privacy policy — ${product}`, description: `How ${product} handles your data.` },
  '/dashboard': { title: `Dashboard — ${product}`, description: '', noindex: true },
  '/api-keys': { title: `API keys — ${product}`, description: '', noindex: true },
  '/usage': { title: `Usage — ${product}`, description: '', noindex: true },
  '/sessions': { title: `Remote sessions — ${product}`, description: '', noindex: true },
  '/payment': { title: `Payment — ${product}`, description: '', noindex: true },
  '/payments-history': { title: `Payment history — ${product}`, description: '', noindex: true },
  '/profile': { title: `Profile — ${product}`, description: '', noindex: true },
  '/verify-email': { title: `Verify your email — ${product}`, description: '', noindex: true },
  '/forgot-password': { title: `Reset password — ${product}`, description: '', noindex: true },
};

/** Public, indexable routes — the sitemap and the crawler hints share this list. */
export const PUBLIC_ROUTES = Object.entries(ROUTE_META)
  .filter(([, m]) => !m.noindex)
  .map(([path]) => path);

/** Resolve metadata for a pathname, falling back to the closest parent route. */
export function metaForPath(pathname: string): RouteMeta {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname];
  const parts = pathname.split('/').filter(Boolean);
  while (parts.length > 0) {
    parts.pop();
    const candidate = '/' + parts.join('/');
    if (ROUTE_META[candidate]) return ROUTE_META[candidate];
  }
  return ROUTE_META['/'];
}

/** Social preview image — served from this origin so previews never break. */
export const OG_IMAGE_PATH = brand.id === 'onbuzz' ? '/img/onbuzz-agents.webp' : '/brands/autopilot/logo.webp';
